import { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { CheckCircle, XCircle } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EmailVerification = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading'); // loading, success, error
  const [message, setMessage] = useState('');
  const calledRef = useRef(false);
  
  useEffect(() => {
    // Guard against React 18 StrictMode double-fire
    if (calledRef.current) return;
    calledRef.current = true;
    verifyEmail();
  }, []);
  
  const verifyEmail = async () => {
    try {
      const response = await axios.get(`${API}/auth/verify-email/${token}`);
      setStatus('success');
      setMessage(response.data.message || 'Email vérifié avec succès !');
      toast.success('Email vérifié ! Vous pouvez maintenant vous connecter.');
    } catch (error) {
      console.error('Error:', error);
      setStatus('error');
      setMessage(error.response?.data?.detail || 'Erreur lors de la vérification');
      toast.error('Erreur de vérification');
    }
  };
  
  if (status === 'loading') {
    return (
      <div 
        className="min-h-screen flex items-center justify-center"
        style={{ background: 'linear-gradient(135deg, var(--bg-tertiary) 0%, var(--bg-secondary) 100%)' }}
        data-testid="email-verification-loading"
      >
        <Card className="w-full max-w-md mx-4">
          <CardHeader className="text-center">
            <Skeleton className="h-16 w-16 rounded-full mx-auto mb-4" />
            <Skeleton className="h-8 w-48 mx-auto" />
            <Skeleton className="h-4 w-64 mx-auto mt-2" />
          </CardHeader>
        </Card>
      </div>
    );
  }
  
  const isSuccess = status === 'success';
  const Icon = isSuccess ? CheckCircle : XCircle;
  const iconColor = isSuccess ? 'var(--success)' : 'var(--error)';
  
  return (
    <div 
      className="min-h-screen flex items-center justify-center"
      style={{ background: 'linear-gradient(135deg, var(--bg-tertiary) 0%, var(--bg-secondary) 100%)' }}
      data-testid={`email-verification-${status}`}
    >
      <Card className="w-full max-w-md mx-4">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 w-16 h-16 rounded-full flex items-center justify-center" style={{ backgroundColor: iconColor }}>
            <Icon className="h-8 w-8 text-white" />
          </div>
          <CardTitle className="text-2xl" style={{ fontFamily: 'var(--font-secondary)', color: iconColor }}>
            {isSuccess ? 'Email vérifié !' : 'Vérification échouée'}
          </CardTitle>
          <CardDescription>
            {message}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {isSuccess ? (
            <Button 
              onClick={() => navigate('/login')}
              className="w-full"
              style={{ backgroundColor: 'var(--primary-400)' }}
              data-testid="goto-login-button"
            >
              Se connecter maintenant
            </Button>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-[var(--text-secondary)] text-center">
                Le lien peut avoir expiré ou être invalide.
              </p>
              <Link to="/login" className="block">
                <Button 
                  className="w-full"
                  style={{ backgroundColor: 'var(--primary-400)' }}
                  data-testid="goto-login-button"
                >
                  Retour à la connexion
                </Button>
              </Link>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default EmailVerification;
